import { colorOfMethod } from '@modules/openCloud/page/main/components/ColoredMethod.styles';
import { makeStyles, TTheme } from '@rbx/ui';
import { CSS_SELECTORS, CSS_CLASS_NAMES } from './tryItOut/constants';

const fontFamilyStyle = {
  fontFamily: `"Builder Sans", Helvetica, Arial, san-serif`,
};

const fontBody1Style = {
  letterSpacing: 'inherit',
  color: 'inherit',
  ...fontFamilyStyle,
  fontWeight: 300,
  fontSize: '16px',
  lineHeight: '140%',
  textDecoration: 'none',
  fontStyle: 'normal',
};

const fontHeaderMediumStyle = {
  letterSpacing: 'inherit',
  color: 'inherit',
  ...fontFamilyStyle,
  fontWeight: 800,
  fontSize: '20px !important',
  lineHeight: '135%',
  textDecoration: 'none',
  fontStyle: 'normal',
};

const httpMethods = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head', 'trace'];

const generateMethodStyles = (
  theme: TTheme,
  classnameCb: (method: string) => string,
  styleCb: (method: string, theme: TTheme) => Record<string, any>,
) => {
  const styles: Record<string, any> = {};
  httpMethods.forEach((method) => {
    styles[classnameCb(method)] = styleCb(method, theme);
  });
  return styles;
};

const methodColorStyles = (theme: TTheme, classnameCb: (method: string) => string) => {
  return generateMethodStyles(theme, classnameCb, (method, localTheme) => ({
    color: colorOfMethod(method, localTheme),
    fontSize: '16px',
    fontFamily: '"Builder Mono", monospace',
    backgroundColor:
      localTheme.palette.mode === 'dark'
        ? localTheme.palette.surface[400]
        : localTheme.palette.states.hover,
  }));
};

const methodBorderColorStyles = (theme: TTheme, classnameCb: (method: string) => string) => {
  return generateMethodStyles(theme, classnameCb, (method, localTheme) => ({
    borderColor: localTheme.palette.content.disabled,
    '&.is-open': {
      borderColor: localTheme.palette.content.muted,
    },
  }));
};

const parameterBackgroundStyles = (theme: TTheme, classnameCb: (method: string) => string) => {
  return generateMethodStyles(theme, classnameCb, () => ({
    background: 'transparent',
  }));
};

const useSwaggerOverrideStyles = makeStyles()((theme) => ({
  container: {
    marginBottom: '24px',
    '& .swagger-ui-overrides': {
      background: 'transparent',
      color: theme.palette.content.standard,
      // blocks
      '.opblock.opblock-patch .opblock-summary': {
        backgroundColor: theme.palette.surface[100],
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
      },
      // accordion summary
      '& .opblock .opblock-summary': {
        padding: `8px !important`,
      },
      '& .opblock.opblock-deprecated .opblock-summary-method': {
        color: theme.palette.content.static.dark,
      },
      '& .opblock .opblock-summary-path, & .opblock .opblock-summary-path *, & .opblock-summary-path__deprecated *':
        {
          color: theme.palette.content.standard,
          fontFamily: '"Builder Mono", monospace !important',
        },
      '& .opblock-summary-description': {
        ...fontBody1Style,
        color: `${theme.palette.content.muted} !important`,
      },
      ...(!process.env.enableTryItOut && {
        // TO HIDE:
        // hide Try it Out test input components, no try it out is available right now
        '& .authorization__btn, & .execute-wrapper': {
          display: 'none !important',
        },
        '& .try-out, & .opblock .parameters-col_description input, & .opblock .opblock-section-header>label':
          {
            display: 'none !important',
          },
        '& .parameters-col_description select': {
          display: 'none !important',
        },
        '& .body-param-options': {
          display: 'none',
        },
        // TO HIDE END
      }),
      '& .opblock .parameter__name.required > span, & .opblock .parameter__name.required:after': {
        fontSize: 14,
        color: theme.palette.content.alert.important,
      },
      '& .opblock .opblock-section-header': {
        color: theme.palette.content.standard,
        background: 'transparent',
      },

      '& .opblock .opblock-section-header h4, & .opblock .opblock-section-header h4 span': {
        ...fontHeaderMediumStyle,
        fontSize: `20px !important`,
        whiteSpace: 'nowrap',
        marginRight: '20px',
      },

      '& .opblock': {
        backgroundColor: `${theme.palette.surface[100]} !important`,
      },
      ...(process.env.enableTryItOut && {
        '& .opblock-summary .authorization__btn': {
          display: 'none !important',
        },
        '& .btn.execute.opblock-control__btn': {
          backgroundColor: theme.palette.actionV2.primaryBrand.fill,
          borderColor: theme.palette.actionV2.primaryBrand.fill,
          '&:hover': {
            backgroundColor: theme.palette.actionV2.primaryBrand.containedHoverFocus,
            borderColor: theme.palette.actionV2.primaryBrand.containedHoverFocus,
          },
        },
        '& .btn.btn-clear.opblock-control__btn': {
          backgroundColor: theme.palette.actionV2.secondary.fill,
          borderColor: theme.palette.actionV2.secondary.fill,
          '&:hover': {
            backgroundColor: theme.palette.actionV2.secondary.containedHoverFocus,
            borderColor: theme.palette.actionV2.secondary.containedHoverFocus,
          },
        },
        [`& .${CSS_CLASS_NAMES.TRY_IT_OUT_BUTTON}`]: {
          '&:hover': {
            backgroundColor: theme.palette.actionV2.secondary.containedHoverFocus,
            borderColor: theme.palette.actionV2.secondary.containedHoverFocus,
          },
        },
        '& .curl.microlight code': {
          whiteSpace: 'pre-wrap !important',
        },
      }),
      '& *:not(.opblock):not(.model) *': {
        ...fontBody1Style,
        '& svg': {
          fill: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        '& .parameter__type, & .prop-format, & .parameter__in': {
          fontFamily: '"Builder Mono", monospace !important',
          fontSize: '14px',
          color: `${theme.palette.content.muted} !important`,
        },
        '& .col_header': {
          fontWeight: 600,
        },
        '& pre.body-param__example': {
          padding: `16px !important`,
        },
        ...(process.env.enableTryItOut && {
          // Hide original Swagger UI try-it-out buttons (replaced with custom try it out buttons)
          [`& ${CSS_SELECTORS.SWAGGER_UI_TRY_IT_OUT_BUTTON}:not(.${CSS_CLASS_NAMES.TRY_IT_OUT_BUTTON})`]:
            {
              display: 'none',
            },
          // Hide body parameter Cancel buttons by default
          [`& ${CSS_SELECTORS.BODY_PARAM_CANCEL_BUTTON}`]: {
            opacity: 0,
            pointerEvents: 'none',
          },
        }),
        '& code': {
          // ref: https://github.rbx.com/Roblox/foundation-web/blob/4bf7a6ba95301260aaeb4ddb5fa1096311d478f2/packages/ui/text/InlineCode.tsx#L11
          backgroundColor: theme.palette.components.inlineCode.asText.fill,
          borderRadius: 4,
          paddingInline: 4,
          padding: '0px 4px',
        },
        '& code, & pre, & pre span': {
          fontFamily: '"Builder Mono", monospace !important',
        },
        '& pre code': {
          backgroundColor: 'transparent',
        },
        '& .renderedMarkdown a': {
          color: theme.palette.content.action,
        },
        '& .renderedMarkdown pre code': {
          backgroundColor: theme.palette.components.inlineCode.asText.fill,
        },
        '& select': {
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.content.static.light
              : theme.palette.content.static.dark,
          backgroundColor: theme.palette.surface[200],
          borderColor: theme.palette.content.disabled,
        },
        ...(process.env.enableTryItOut && {
          '& input[type="text"], & input[type="password"], & input[type="email"], & input[type="number"], & input[type="file"], & textarea':
            {
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.content.static.light
                  : theme.palette.content.static.dark,
              backgroundColor: theme.palette.surface[200],
              borderColor: theme.palette.content.disabled,
              '&::placeholder': {
                color: theme.palette.content.muted,
              },
            },
        }),
        '&[data-name="examplePanel"]': {
          borderRadius: 8,
          overflow: 'hidden',
        },
        // code background
        '& .highlight-code': {
          borderRadius: 12,
        },
        '& .parameter__in': {
          fontStyle: 'italic',
          color: theme.palette.content.disabled,
        },
        '& .body-param-options': {
          marginTop: '8px',
        },
        '& .content-type-wrapper.body-param-content-type': {
          marginTop: 4,
        },
        '& .opblock-description': {
          h4: {
            fontSize: `18px !important`,
            fontWeight: 500,
          },
          h5: {
            fontSize: `16px !important`,
            fontWeight: 500,
          },
          hr: {
            border: `1px solid ${theme.palette.content.disabled}`,
            margin: '16px 0',
          },
          a: {
            color: theme.palette.content.action,
          },
        },
      },

      '& .response .response-col_status, & .response .response-col_description': {
        borderTop: `1px solid rgba(59,65,81,.2)`,
        paddingBottom: 24,
      },
      // actions

      // Example Value, Model buttons
      '& .tab li button.tablinks': {
        // padding: '4px',
        // background: theme.palette.surface[200]
        textDecoration: 'none',
        paddingBottom: 3,
        '&:hover': {
          textDecoration: 'underline',
        },
      },

      '& .tab li.tabitem.active button': {
        fontWeight: 600,
        textDecoration: 'underline',
      },

      '& .opblock-body pre.microlight': {
        fontSize: 14,
        fontFamily: '"Builder Mono", monospace !important',
        background: `${
          theme.palette.mode === 'dark'
            ? theme.palette.surface[200]
            : theme.palette.content.static.dark
        } !important`,
        color: theme.palette.content.static.light,
      },

      // Method labels in Roblox colors
      ...methodColorStyles(
        theme,
        (method) => `& .opblock.opblock-${method} .opblock-summary-method`,
      ),
      ...methodBorderColorStyles(
        theme,
        (method) => `& .opblock.opblock-${method} .opblock-summary, & .opblock.opblock-${method}`,
      ),
      ...parameterBackgroundStyles(
        theme,
        (method) => `& .opblock.opblock-${method} .tab-header .tab-item.active h4 span:after`,
      ),

      // Models section
      '& .model-box': {
        width: '100%',
        background: theme.palette.surface[200],
        borderRadius: 12,
      },
      '& .model-container .model-box': {
        width: 'auto',
      },
      '& .models h4:hover': {
        background: 'transparent',
      },
      '& .models, & .model-box': {
        '& .prop-type': {
          fontFamily: '"Builder Mono", monospace !important',
          fontSize: '14px',
          color: `${theme.palette.content.muted}`,
        },
        '& .property.primitive': {
          color: `${theme.palette.content.muted}`,
          display: 'inline-block',
          paddingLeft: '4px',
        },
      },
      '& .model': {
        '& .description .markdown p': {
          margin: 0,
        },
        '& .prop .markdown': {
          margin: '4px 0',
          '& p': {
            margin: 0,
          },
        },
        '& .description > td:first-child': {
          textTransform: 'capitalize',
          fontWeight: 300,
        },
        '& .property-row > td:first-child': {
          fontFamily: '"Builder Mono", monospace !important',
          fontSize: '14px',
        },
        '& td:first-of-type': {
          padding: 0,
        },
        '& tr.property-row': {
          '& td:first-child, & p, & .prop-enum': {
            paddingRight: 4,
            paddingLeft: 8,
          },
          '& .renderedMarkdown p': {
            minWidth: '300px',
          },
        },
      },
      '& .model-container, & .model-toggle': {
        background: `${theme.palette.surface[200]} !important`,
      },
      '& .model-toggle:after': {
        background: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cpath d='M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' fill='${
          theme.palette.mode === 'dark' ? 'white' : 'black'
        }'/%3E%3C/svg%3E") 50% no-repeat;`,
      },
    },
  },
}));

export default useSwaggerOverrideStyles;
